import React, { useState, useEffect, useRef } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { UserButton, useUser, useClerk } from "@clerk/clerk-react";
import { LayoutGrid, Film, Building2, Calendar, MessageSquare, Users, RefreshCw, MoreHorizontal } from "lucide-react";
import BrandLogo from "../common/BrandLogo";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../styles/admin-global.css";
import "../../styles/admin-responsive.css";


const AdminLayout = ({ children }) => {
  const location = useLocation();
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const moreMenuRef = useRef(null);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target)) {
        setShowMoreMenu(false);
      }
    };
    if (showMoreMenu) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showMoreMenu]);



  const moreicon = (
    <svg
      className="me-2"
      width="24"
      height="24"
      fill="none"
      viewBox="0 0 24 24"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="2"
        d="M12 5v14m7-7H5"
      />
    </svg>
  );

  const sidebarLinks = [
    { name: "Dashboard", path: "/admin", icon: LayoutGrid },
    { name: "Movies", path: "/admin/movies", icon: Film },
    { name: "Theaters", path: "/admin/theaters", icon: Building2 },
    { name: "Shows", path: "/admin/shows", icon: Calendar },
    { name: "Bookings", path: "/admin/bookings", icon: MessageSquare },
    { name: "Users", path: "/admin/users", icon: Users },
    { name: "Data Sync", path: "/admin/data-sync", icon: RefreshCw },
  ];

  // Logout is handled directly in the navbar

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#1f2025",
      }}
    >
      {/* Header */}
      <div
        className="d-flex align-items-center justify-content-between px-2 px-md-4 py-2 py-md-3 border-bottom"
        style={{
          height: isMobile ? "60px" : "70px",
          flexShrink: 0,
          background: "rgb(31, 32, 37)",
          borderColor: "rgb(55, 65, 81)",
          position: isMobile ? "fixed" : "relative",
          top: isMobile ? "0" : "auto",
          left: isMobile ? "0" : "auto",
          right: isMobile ? "0" : "auto",
          zIndex: isMobile ? 1001 : "auto",
        }}
      >
        <div className="d-flex align-items-center">
          <Link to="/" style={{ textDecoration: "none" }}>
            <BrandLogo
              className={isMobile ? "me-2" : "me-3"}
              style={{ transform: isMobile ? "scale(0.85)" : "none" }}
            />
          </Link>
          <div className="d-none d-sm-block">
            <h5
              className="text-white mb-0"
              style={{ fontSize: isMobile ? "0.9rem" : "1.25rem" }}
            >
              Admin
            </h5>
            <small
              className="text-light"
              style={{ fontSize: isMobile ? "0.7rem" : "0.875rem" }}
            >
              Management Panel
            </small>
          </div>
        </div>
        <div className="navbar-actions d-flex align-items-center">
          <div className="me-3 d-none d-md-block">
            <div className="text-end">
              <div className="text-white">
                {clerkUser?.firstName || "Admin"}
              </div>
              <small className="text-light">
                {clerkUser?.primaryEmailAddress?.emailAddress}
              </small>
            </div>
          </div>
          <div className="d-flex align-items-center">
            <UserButton
              appearance={{
                elements: {
                  avatarBox: "admin-user-avatar",
                  userButtonPopoverCard: "admin-clerk-dropdown",
                },
              }}
            />
            {/* <button
              className="btn btn-outline-danger btn-sm ms-2"
              onClick={handleLogout}
              title="Logout"
            >
              <FaSignOutAlt />
            </button> */}
          </div>
        </div>
      </div>

      <div
        className="d-flex"
        style={{
          height: "100vh",
        }}
      >
        {/* Desktop Sidebar */}
        {!isMobile && (
          <div
            style={{
              width: "250px",
              background: "#1f2025",
              borderRight: "1px solid #374151",
              position: "fixed",
              height: isMobile ? "calc(100vh - 60px)" : "calc(100vh - 70px)",
              zIndex: 1000,
            }}
          >
            <div className="pt-4">
              {sidebarLinks.map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    to={item.path}
                    key={index}
                    className="d-flex align-items-center text-decoration-none py-3 px-4 position-relative"
                    style={{
                      color: isActive ? "#e63946" : "#9ca3af",
                      background: isActive
                        ? "linear-gradient(135deg, #e63946, #f84565) !important"
                        : "transparent",
                      borderRight: isActive ? "4px solid #e63946" : "none",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        e.target.style.backgroundColor =
                          "rgba(255,255,255,0.05)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        e.target.style.backgroundColor = "transparent";
                      }
                    }}
                  >
                    <item.icon className="me-2" size={24} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div
          className="flex-grow-1 admin-page-wrapper"
          style={{
            background: "#1f2025",
            marginLeft: isMobile ? "0" : "250px",
            height: isMobile ? "100vh" : "calc(100vh - 70px)",
            overflowY: "auto",
            paddingBottom: isMobile ? "80px" : "0",
            paddingTop: isMobile ? "70px" : "0",
          }}
        >
          <div className="p-3">{children}</div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <div
          className="position-fixed bottom-0 start-0 w-100 bg-dark border-top"
          style={{
            zIndex: 1000,
            borderColor: "#374151 !important",
            background: "#1f2025 !important",
          }}
        >
          <div className="d-flex justify-content-around py-2">
            {sidebarLinks.slice(0, 5).map((item, index) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  to={item.path}
                  key={index}
                  className="d-flex flex-column align-items-center text-decoration-none py-2 px-1"
                  style={{
                    color: isActive ? "#e63946" : "#9ca3af",
                    fontSize: "0.75rem",
                    minWidth: "60px",
                  }}
                >
                  <div className="mb-1">
                    <item.icon size={20} />
                  </div>
                  <span style={{ fontSize: "0.7rem" }}>{item.name}</span>
                </Link>
              );
            })}
            {/* More Menu */}
            <div
              ref={moreMenuRef}
              className="position-relative"
              style={{ minWidth: "60px" }}
            >
              <button
                onClick={() => setShowMoreMenu(!showMoreMenu)}
                className="d-flex flex-column align-items-center text-decoration-none py-2 px-1 border-0 bg-transparent"
                style={{
                  color: "#9ca3af",
                  fontSize: "0.75rem",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                <div className="mb-1">
                  <MoreHorizontal size={20} />
                </div>
                <span style={{ fontSize: "0.7rem" }}>More</span>
              </button>
              {showMoreMenu && (
                <div
                  className="position-absolute bottom-100 end-0 bg-dark border rounded-2"
                  style={{
                    borderColor: "#374151",
                    minWidth: "120px",
                    marginBottom: "8px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  {sidebarLinks.slice(5).map((item, index) => {
                    const isActive = location.pathname === item.path;
                    return (
                      <Link
                        to={item.path}
                        key={index}
                        onClick={() => setShowMoreMenu(false)}
                        className="d-flex align-items-center text-decoration-none py-2 px-3"
                        style={{
                          color: isActive ? "#e63946" : "#9ca3af",
                          fontSize: "0.875rem",
                          borderBottom: index < sidebarLinks.slice(5).length - 1 ? "1px solid #374151" : "none",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) {
                            e.target.style.backgroundColor = "rgba(255,255,255,0.05)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) {
                            e.target.style.backgroundColor = "transparent";
                          }
                        }}
                      >
                        <div className="me-2">
                          <item.icon size={16} />
                        </div>
                        <span>{item.name}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
